/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { TaskModifierType } from '@kaiizen/kai-pro-api';
import { useEffect, useMemo, useState } from 'react';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import {
  MODAL_BASE_PRIMARY_BUTTON_STYLE,
  MODAL_BASE_SECONDARY_BUTTON_STYLE,
  ModalBase,
} from '@/Client/Widgets/ModalBase';
import { styleguideFontFamily as FONT, styleguideLetterSpacing as TRACKING } from '@kaiizen/kai-shiki/core';
import {
  MODIFIER_DUPLICATE_LABEL_ERROR,
  MODIFIER_LABEL_MAX_LENGTH,
  MODIFIER_TOTAL_MAX,
  MODIFIER_TYPE_COPY,
  normalizeModifierLabel,
} from './modifierTypeCopy';

const MODIFIER_TYPE_ORDER: TaskModifierType[] = ['resident', 'deferred', 'display'];

const STEP_LABEL_STYLE = css`
  label: ADD_MODIFIER_STEP;
  margin: 0 0 4px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  /* Wider than the mono tokens carry; the design tracks these step labels further apart than
     the mono captions elsewhere. */
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
`;

const STEP_NOTE_STYLE = css`
  label: ADD_MODIFIER_STEP_NOTE;
  margin: 0 0 12px;
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  color: ${COLORS.midtone};
`;

const TYPE_LIST_STYLE = css`
  label: ADD_MODIFIER_TYPES;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 26px;
`;

const TYPE_STYLE = css`
  label: ADD_MODIFIER_TYPE;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: 15px 17px;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;

  &:hover {
    border-color: ${COLORS.midtone};
  }
`;

/* The lemon selection tint and an ink border, from the styleguide. This read
   `infoBlueBackground`/`infoBlue` — a legacy carry-forward blue that is not in this design
   language at all, which is what made the picker look wrong next to the rest of the page
   (#2787 review). */
const TYPE_PICKED_STYLE = css`
  label: ADD_MODIFIER_TYPE_PICKED;
  background: ${COLORS.selectedWash};
  border-color: ${COLORS.ink};
`;

/* A radio has to show which option is chosen. Without a mark, the only difference between
   picked and unpicked was the card's tint, which is why this read as an unstyled list. */
const TYPE_MARK_STYLE = css`
  label: ADD_MODIFIER_TYPE_MARK;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 2px;
  border: 1.5px solid ${COLORS.midtone};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TYPE_MARK_PICKED_STYLE = css`
  label: ADD_MODIFIER_TYPE_MARK_PICKED;
  border-color: ${COLORS.ink};
  background: ${COLORS.ink};
  color: ${COLORS.accent};
`;

/** At capacity stays visible — an admin should see the limit, not wonder where the type went. */
const TYPE_FULL_STYLE = css`
  label: ADD_MODIFIER_TYPE_FULL;
  opacity: 0.5;
  cursor: not-allowed;
`;

const TYPE_NAME_STYLE = css`
  label: ADD_MODIFIER_TYPE_NAME;
  margin: 0 0 2px;
  font-size: ${pxToRem(FONT_SIZE.bodyInput)};
  font-weight: ${FONT_WEIGHT.bold};

  /* The kind rides in the heading at body size, as the card titles do. */
  span {
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodySmall)};
    font-weight: ${FONT_WEIGHT.bold};
  }
`;

const TYPE_BODY_STYLE = css`
  label: ADD_MODIFIER_TYPE_BODY;
  margin: 2px 0 0;
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  line-height: 1.45;
  color: ${COLORS.midtone};
`;

const CAPACITY_STYLE = css`
  label: ADD_MODIFIER_CAPACITY;
  margin-left: auto;
  flex-shrink: 0;
  align-self: center;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${TRACKING.monoCaption};
  text-transform: uppercase;
  white-space: nowrap;
  color: ${COLORS.midtone};
`;

const LABEL_INPUT_STYLE = css`
  label: ADD_MODIFIER_LABEL_INPUT;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${COLORS.stroke};
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  color: ${COLORS.ink};

  &::placeholder {
    color: ${COLORS.midtone};
    opacity: 0.75;
  }
`;

const ERROR_STYLE = css`
  label: ADD_MODIFIER_ERROR;
  margin: 8px 0 0;
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  color: ${COLORS.error};
`;

const USED_STYLE = css`
  label: ADD_MODIFIER_USED;
  font-size: ${pxToRem(FONT_SIZE.bodyMicro)};
  color: ${COLORS.midtone};
`;

export type AddModifierSubmission = { type: TaskModifierType; label: string };

export const AddModifierModal = ({
  usedByType,
  totalUsed,
  existingLabels,
  onCancel,
  onAdd,
}: {
  usedByType: Record<TaskModifierType, number>;
  totalUsed: number;
  existingLabels: readonly string[];
  onCancel: () => void;
  onAdd: (submission: AddModifierSubmission) => void;
}) => {
  const [pickedType, setPickedType] = useState<TaskModifierType | undefined>(undefined);
  const [label, setLabel] = useState('');

  const takenLabels = useMemo(() => new Set(existingLabels.map(normalizeModifierLabel)), [existingLabels]);
  const trimmedLabel = label.trim();
  const isDuplicate = !!trimmedLabel && takenLabels.has(normalizeModifierLabel(label));
  const canAdd = !!pickedType && !!trimmedLabel && !isDuplicate;

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onCancel]);

  return (
    <ModalBase
      actions={
        <>
          <button css={MODAL_BASE_SECONDARY_BUTTON_STYLE} onClick={onCancel} type='button'>Cancel</button>
          <button
            css={MODAL_BASE_PRIMARY_BUTTON_STYLE}
            disabled={!canAdd}
            onClick={() => pickedType && onAdd({ type: pickedType, label: trimmedLabel })}
            type='button'
          >
            Add Modifier
          </button>
        </>
      }
      maxWidth={560}
      onOverlayClick={onCancel}
      title='Add Modifier'
    >
      <p css={USED_STYLE}>{`${totalUsed} of ${MODIFIER_TOTAL_MAX} used`}</p>

      <p css={STEP_LABEL_STYLE}>Modifier type</p>
      <p css={STEP_NOTE_STYLE}>Kai sets the financial behavior for each type. Pick the one this modifier should follow.</p>

      <div aria-label='Modifier type' css={TYPE_LIST_STYLE} role='radiogroup'>
        {MODIFIER_TYPE_ORDER.map(type => {
          const copy = MODIFIER_TYPE_COPY[type];
          const used = usedByType[type];
          const isFull = used >= copy.max || totalUsed >= MODIFIER_TOTAL_MAX;
          return (
            <button
              aria-checked={pickedType === type}
              aria-disabled={isFull}
              css={[TYPE_STYLE, pickedType === type && TYPE_PICKED_STYLE, isFull && TYPE_FULL_STYLE]}
              key={type}
              onClick={() => !isFull && setPickedType(type)}
              role='radio'
              type='button'
            >
              <span
                aria-hidden='true'
                css={[TYPE_MARK_STYLE, pickedType === type && TYPE_MARK_PICKED_STYLE]}
              >
                {pickedType === type ? '✓' : null}
              </span>
              <span>
                <h4 css={TYPE_NAME_STYLE}>
                  {copy.name} <span>{`(${copy.kind})`}</span>
                </h4>
                <p css={TYPE_BODY_STYLE}>{copy.meta ?? copy.lockSub}</p>
              </span>
              <span css={CAPACITY_STYLE}>{`${used} of ${copy.max} used`}</span>
            </button>
          );
        })}
      </div>

      <p css={STEP_LABEL_STYLE}>Modifier label</p>
      <input
        aria-label='Modifier label'
        css={LABEL_INPUT_STYLE}
        maxLength={MODIFIER_LABEL_MAX_LENGTH}
        onChange={event => setLabel(event.target.value)}
        placeholder='Enter custom label. This is what your team will see and filter by.'
        type='text'
        value={label}
      />
      {isDuplicate ? <p css={ERROR_STYLE} role='alert'>{MODIFIER_DUPLICATE_LABEL_ERROR}</p> : null}
    </ModalBase>
  );
};
